import { Badge } from 'primereact/badge';
import { TreeNode } from 'primereact/treenode';
import {
  TreeSelect,
  TreeSelectChangeEvent,
  TreeSelectCheckboxSelectionKeyType,
  TreeSelectSelectionKeysType,
} from 'primereact/treeselect';
import { classNames } from 'primereact/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductsTableQueryParams } from '../../../../hooks/useProductsTableQueryParams';
import { parseMulti } from '../../../../shared/helpers/url-parameter-parser';
import { ITag } from '../../../../shared/interfaces/ITag';
import styles from './TagsFilter.module.css';

interface SelectedNode {
  [key: string]: TreeSelectCheckboxSelectionKeyType;
}

interface TagsFilterProps extends React.ComponentProps<'div'> {
  companyTags: ITag[];
}

const TagsFilter = ({ companyTags }: TagsFilterProps) => {
  const { t } = useTranslation();
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const [selectedTags, setSelectedTags] = useState<SelectedNode>({});

  const { filters, setParams } = useProductsTableQueryParams();

  // Based on the filter values that initially come from the parent component (from URL actually), set the selected nodes
  useEffect(() => {
    prepareInitialNodeKeys();
  }, []);

  // Convert your flat tags to TreeNode array
  const treeNodes: TreeNode[] = companyTags?.map((tag) => {
    return {
      key: tag.id.toString(),
      label: tag.name,
    };
  });

  const prepareInitialNodeKeys = (): void => {
    // Get the array of tags keys from parent, i.e. the URL
    const tagKeys = parseMulti(filters.tagId);

    // Map to store the selected nodes to be set in the state, to show them selected in the tree
    const preSelectedNodeKeys: SelectedNode = {};

    tagKeys.forEach((tagKey: string) => {
      preSelectedNodeKeys[tagKey] = { checked: true, partialChecked: false };
    });
    // Now highlight those nodes in the tree
    setSelectedTags(preSelectedNodeKeys);
  };

  const handleChange = (e: TreeSelectChangeEvent) => {
    const selectedKeysMap = (e.value as SelectedNode) || {};

    setSelectedTags(selectedKeysMap);

    // Map back to your original object structure
    const selectedItems: string[] = [];

    Object.keys(selectedKeysMap).forEach((key) => {
      selectedItems.push(key);
    });

    setParams({ tagId: selectedItems, page: 1 });
  };

  const valueTemplate = (nodes: TreeNode[] | TreeNode | null | undefined) => {
    // If nothing is selected, show the placeholder
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return (
        <span className='line-height-2'>{t('productsPage.filters.tagsFilter.placeholder')}</span>
      );
    }

    // Display the number of selected nodes
    return (
      <div className='flex flex-row gap-1 line-height-2'>
        <span className='text-primary'>{t('productsPage.filters.tagsFilter.placeholder')}:</span>
        <Badge className='flex align-items-center justify-content-center' value={nodes.length} />
      </div>
    );
  };

  // Custom template for items with tick icon
  const nodeTemplate = (node: TreeNode) => {
    // Map back to your original object structure
    const selectedItems: string[] = [];

    Object.keys(selectedTags).forEach((key) => {
      selectedItems.push(key);
    });

    const isSelected = selectedItems.some((selectedItem) => {
      return selectedItem === node.key;
    });

    return (
      <div className='flex-1 w-full p-2'>
        <div className='flex flex-row justify-content-between align-items-center'>
          <span className={` font-normal break-all ${isSelected ? 'text-primary' : ''}`}>
            {node.label}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={classNames('flex align-items-center', styles['tag-filter'])}>
      <TreeSelect
        value={selectedTags as TreeSelectSelectionKeysType}
        options={treeNodes}
        selectionMode='multiple'
        valueTemplate={valueTemplate}
        nodeTemplate={nodeTemplate}
        onChange={handleChange}
        onHide={() => {
          setIsOverlayVisible(false);
        }}
        showClear
        onShow={() => {
          setIsOverlayVisible(true);
        }}
        filterPlaceholder={t('productsPage.filters.tagsFilter.filterPlaceholder')}
        filter
        filterInputAutoFocus
        className={classNames(
          styles['tag-filter'],
          isOverlayVisible ? styles['tag-filter-open'] : styles['tag-filter-close'],
          'border-none',
        )}
        pt={{
          root: {
            className: styles['root'],
          },
          labelContainer: {
            className: 'max-h-full w-full',
          },
          label: {
            className: styles['label'],
          },
          closeButton: {
            className: 'hidden',
          },
          wrapper: {
            className: styles['tag-filter'],
          },
          filterContainer: {
            className: styles['filter-container'],
          },
          clearIcon: {
            className: classNames(
              'text-black-alpha-60',
              {
                hidden: selectedTags === undefined || Object.keys(selectedTags).length === 0,
              },
              styles['clear-icon'],
            ),
          },
          trigger: {
            className: classNames({
              'hidden w-0 p-0 overflow-hidden shrink-0':
                selectedTags && Object.keys(selectedTags).length > 0,
            }),
          },
        }}
      />
    </div>
  );
};

export default TagsFilter;
